import { accumulate, observableSelector } from '../../helpers';
import { AlkLogger } from '@alk/core';
import { arrayItemsMapper } from '../../helpers/array-utils';
import {
    buildRequestByConfig,
    combineConfigWithLoadedInstruments,
    combineForwardOrderRequest,
    combinePendingOrderRequest,
    combineSpotOrderRequest,
    orderToNotification,
    padOptionsTransformer,
    resolvedTenorMapper,
    tenorsToResolve,
    toSimpleNotificationType,
    transformQuoteResponse,
    transformSecurityItemWithType,
} from './pad-config.service.utils';
import {
    catchError,
    delay,
    filter,
    finalize,
    first,
    map,
    scan,
    shareReplay,
    skip,
    switchMap,
    take,
    takeUntil,
    tap,
    withLatestFrom,
} from 'rxjs/operators';
import { combineLatest, merge, Observable, of, ReplaySubject, Subject } from 'rxjs';
import { CONFIGURATION_SERVICE, IConfigurationService } from '../configuration';
import {
    ExtractionResult,
    ExtractionSelector,
    FULL_STATE,
    IPadConfig,
    IPadResult,
    IQuoteRequest,
    ISimpleNotification,
    IStartedPadConfig,
    IStreamError,
    PadConfigExtended,
} from '../../interfaces';
import { FORWARD_TYPE, IPadConfigService } from '.';
import { IInstrumentsService, INSTRUMENTS_SERVICE } from '../instruments';
import { ILayoutPadsService, LAYOUT_PADS_SERVICE } from '../layout-pads';
import { inject, injectable } from 'inversify';
import { IPadOptionsService, PAD_OPTIONS_SERVICE } from '../pad-options';
import { IPlaceOrder, PLACE_ORDER_SERVICE } from '../place-order';
import { IPriceProviderService, PRICE_PROVIDER_SERVICE } from '../price-provider';
import { ISpeedBumpService, SPEED_BUMP_SERVICE } from '../speed-bump';
import { ISubscriptionPoolingService, SUBSCRIPTION_POOLING_SERVICE } from '../pricing/subscription-pooling';
import { ITenorResolveService, TENOR_RESOLVE_SERVICE } from '../tenor-resolve';
import { QuoteSideTypeEnum, SecuritySymbol } from '@fnx/fx-pad';
import { rulesAccumulator } from './pad-config.service.state.rules';
import { simpleMessageConfiguration } from '../../helpers/simple-message/simple-message';
import { stringifyPlainProps } from '../../helpers/object-utils';

const AWAITING_FOR_QUOTES_DELAY = 600;

@injectable()
export class PadConfigService implements IPadConfigService {

    private readonly DEFAULT_CONFIG_EXTENTION: Partial<IPadResult> = {
        quote: undefined,
        resolvedTenors: [],
    };

    private readonly orderRequestsObs = new Subject<QuoteSideTypeEnum>();

    private readonly notificationObs = new Subject<ISimpleNotification>();

    private readonly identifiersObs = new ReplaySubject<string>(1);

    private _loadedConfigObs: Observable<IPadConfig>;

    private readonly loadedConfigObs = (): Observable<IPadConfig> =>
        this._loadedConfigObs ?? (this._loadedConfigObs = this.identifiersObs.pipe(
            takeUntil(this.stopped$),
            finalize(() => this._loadedConfigObs = null),
            switchMap(id => this.layoutPadsSrv.padsObservable().pipe(
                map(pads => pads.find(pad => pad.id === id)),
                first(),
            )),
        ));

    private readonly _configSub = new Subject<Partial<IStartedPadConfig>>();

    private _configObs: Observable<PadConfigExtended>;
    private readonly configObs = (): Observable<PadConfigExtended> =>
        this._configObs ?? (this._configObs =
                this.loadedConfigObs().pipe(
                    takeUntil(this.stopped$),
                    finalize(() => this._configObs = null),
                    switchMap(loaded => {
                        const instruments$ = this.instrumentsService.instrumentsObservable()
                            .pipe(
                                map(arrayItemsMapper(transformSecurityItemWithType)),
                                map(instruments => ({ allInstruments: instruments })),
                            );

                        return merge(
                            this._configSub,
                            // NOTE: At initialisation, we need to combine the instruments
                            // with the loaded config for the given Pad.
                            instruments$.pipe(
                                take(1),
                                map(obj => combineConfigWithLoadedInstruments(loaded, obj)),
                            ),
                            // NOTE: Once the initialisation step is done, we can go back
                            // to a simple listening of the instrument list.
                            instruments$.pipe(
                                skip(1),
                            ),
                        ).pipe(
                            scan((accumulatedValue, changeSet) => ({
                                ...accumulatedValue,
                                ...rulesAccumulator(accumulatedValue, changeSet),
                            }), {} as PadConfigExtended),
                            tap(this.updateLayout),
                        );
                    }),
                    shareReplay(1),
                )
        );

    private readonly updateLayout = (pad: PadConfigExtended): void => {
        const { isStartRequested, allInstruments, instruments, instrument, ...propertiesToPersist } = pad;
        this.layoutPadsSrv.update({
            instrument: { symbol: instrument.symbol } as SecuritySymbol,
            ...propertiesToPersist,
        });
    };

    public origConfigObservable = <S extends ExtractionSelector<PadConfigExtended>>(request: S):
    Observable<ExtractionResult<PadConfigExtended, S>> =>
        this.configObs().pipe(observableSelector(request));

    private readonly origConfigChange = (config: Partial<IStartedPadConfig>) =>
        this._configSub.next(config);

    private readonly origSetConfigProperty = <T extends keyof IStartedPadConfig>(prop: T, value: IStartedPadConfig[T]) =>
        this.origConfigChange({ [ prop ]: value });

    private _resultStateObs: Observable<IPadResult>;

    private readonly resultStateObs = (): Observable<IPadResult> =>
        this._resultStateObs ?? (this._resultStateObs = merge(
            this.origConfigObservable(FULL_STATE),
            this.getPriceObs(),
            this.getTenorResolveObs(),
            this.getConfigObs(),
            this.getNotificationObs(),
            this.getSpeedBumpActiveObs(),
        ).pipe(
            takeUntil(this.stopped$),
            accumulate(this.DEFAULT_CONFIG_EXTENTION as IPadResult),
            shareReplay(1),
        ));

    private readonly getSpeedBumpActiveObs = (): Observable<Partial<IPadResult>> => {
        return this.speedBumpSrv.speedBumpStateObservable().pipe(
            map(isSpeedBumpActive => ({
                isSpeedBumpActive,
            })),
        );
    };

    public configObservable = <S extends ExtractionSelector<IPadResult>>(request: S):
    Observable<ExtractionResult<IPadResult, S>> =>
        this.resultStateObs().pipe(observableSelector(request));

    private readonly stopped$ = new Subject<boolean>();

    @inject(INSTRUMENTS_SERVICE) private readonly instrumentsService: IInstrumentsService;
    @inject(TENOR_RESOLVE_SERVICE) private readonly tenorsResolveService: ITenorResolveService;
    @inject(PLACE_ORDER_SERVICE) private readonly placeOrderService: IPlaceOrder;
    @inject(PRICE_PROVIDER_SERVICE) private readonly priceProviderService: IPriceProviderService;
    @inject(LAYOUT_PADS_SERVICE) private readonly layoutPadsSrv: ILayoutPadsService;
    @inject(PAD_OPTIONS_SERVICE) private readonly padOptionsSrv: IPadOptionsService;
    @inject(CONFIGURATION_SERVICE) private readonly configurationSrv: IConfigurationService;
    @inject(SPEED_BUMP_SERVICE) private readonly speedBumpSrv: ISpeedBumpService;
    @inject(SUBSCRIPTION_POOLING_SERVICE) private readonly subscriptionPoolingSrv: ISubscriptionPoolingService;

    private readonly getPriceObs = (): Observable<Partial<IPadResult>> => {
        let currRequest: IQuoteRequest = undefined;
        let hasFailed = false;
        let hasEverReceivedQuotes = false;

        const source$ = this.origConfigObservable([
            'instrument',
            'tradedCurrency',
            'quantity',
            'quantitySecondLeg',
            'spotType',
            'pendingType',
            'date',
            'productType',
            'dateFar',
            'farQuantity',
            'instruments',
            'isStartRequested',
            'selectedTradeSide',
            'triggerPrice',
        ]).pipe(
            // NOTE: The following takeUntil is here to prevent the quote stream from resuming on instruments notifications
            // when the service is stopped. This is due to some side effect from the shareReplay and should not happen normally
            // More details about the issue could be found here: https://blog.strongbrew.io/share-replay-issue/
            // Sadly I was not able to test case it :(
            takeUntil(this.stopped$),
            // TODO: Why not using a filter here instead of returning an undefined request ?
            map(buildRequestByConfig),
            filter(request => stringifyPlainProps(request) !== stringifyPlainProps(currRequest)),
            shareReplay(),
        );

        const response$ = combineLatest([
            source$,
            this.subscriptionPoolingSrv.shouldResubscribeObservable(),
        ]).pipe(
            map(([ request ]) => request),
            switchMap(request => {
                currRequest = request;

                return this.priceProviderService.quoteObservable(request, this.handleQuoteError).pipe(
                    // NOTE: The following takeUntil is here to stop quote streaming when the service is stopped.
                    // Having a shareReplay without this takUntil, zombies remain and prevent proper unsubcription to the server
                    // This is due to some side effect from the shareReplay and should not happen normally
                    // More details about the issue could be found here: https://blog.strongbrew.io/share-replay-issue/
                    // Sadly I was not able to test case it :(
                    takeUntil(this.stopped$),
                    tap((response) => {
                        if (response.isStarted) {
                            hasEverReceivedQuotes = true;
                        }
                    }),
                    catchError((error: IStreamError) => {
                        if ([ 'forward', 'swap' ].includes(request?.productType)) {
                            hasFailed = true;
                            if (hasEverReceivedQuotes) {
                                this.notificationObs.next(simpleMessageConfiguration.NO_MORE_QUOTES);
                            } else if (Boolean(error.message)) {
                                this.notificationObs.next({
                                    type: toSimpleNotificationType(error.severity),
                                    message: error.message,
                                    isEphemere: error.isAutoCloseMessage ?? true,
                                });
                            } else {
                                this.notificationObs.next(simpleMessageConfiguration.QUOTE_ERROR);
                            }
                            this.toggleFeed(false);
                        }
                        return of({});
                    }),
                    finalize(() => {
                        if (!hasFailed && Boolean(request) &&
                            [ 'forward', 'swap' ].includes(request?.productType) &&
                            request === currRequest) {
                            this.notificationObs.next(simpleMessageConfiguration.QUOTE_ENDED);
                            this.toggleFeed(false);
                        }
                    }),
                );
            }),
            map(transformQuoteResponse),
            shareReplay(),
        );

        // NOTE: This part of the code handle the awaiting quotes toast that
        // need to be display only when the quotes take time to arrive
        source$.pipe(
            delay(AWAITING_FOR_QUOTES_DELAY),
            withLatestFrom(response$),
            filter(([ request, response ]) => {
                return [ 'forward', 'swap' ].includes(request?.productType) &&
                    !response.isQuoteFeedAlive;
            }),
            filter(() => !hasFailed && !hasEverReceivedQuotes),
        ).subscribe(() => {
            this.notificationObs.next(simpleMessageConfiguration.AWAITING_QUOTES);
        });

        source$.pipe(
            filter(Boolean),
        ).subscribe(() => {
            hasFailed = false;
            hasEverReceivedQuotes = false;
        });

        return response$;
    };

    private getTenorResolveObs(): Observable<Partial<IPadResult>> {
        const tenorsToDisplay$ = this.configurationSrv.asObservable()
            .pipe(
                map(config => config.tenors),
            );

        return combineLatest(
            this.origConfigObservable([ 'instrument', 'date', 'dateFar' ]),
            tenorsToDisplay$,
        ).pipe(
            map(([ { instrument, date, dateFar }, predefinedTenors ]) => {
                return tenorsToResolve(instrument?.symbol, predefinedTenors, [ date, dateFar ]);
            }),
            filter((tenors) => tenors.length > 0),
            switchMap((tenors) => this.tenorsResolveService.resolvedTenorObservable(tenors)),
            map(resolvedTenors => ({
                resolvedTenors: resolvedTenors.map(resolvedTenorMapper),
            })),
        );
    }

    private readonly getConfigObs = (): Observable<Partial<IPadResult>> =>
        this.padOptionsSrv.configObs().pipe(
            map(padOptionsTransformer),
        );

    private readonly handleQuoteError = (errMsg: string): void => {
        AlkLogger.debug(`[PAD_CONFIG_SERVICE#handleQuoteError] got error: ${ errMsg }, stream halted`);
    };

    private readonly getNotificationObs = (): Observable<Partial<IPadResult>> => {
        return this.notificationObs
            .pipe(
                // NOTE: We need to change the object reference if we want to
                // pass the distinctUntilChanged operator.
                map(notif => ({
                    notification: { ...notif },
                })),
            );
    };

    start(id: string): void {

        this.identifiersObs.next(id);
        this.orderRequestsObs.pipe(
            takeUntil(this.stopped$),
            withLatestFrom(this.configObservable(FULL_STATE)),
            filter(([ , state ]) => state.productType === 'spot'),
            map(combineSpotOrderRequest),
        ).subscribe(async (requestOrderConfig) => {
            this.notificationObs
                .next(orderToNotification(await this.placeOrderService.sendSpotOrderRequest(requestOrderConfig)));
        });

        this.orderRequestsObs.pipe(
            takeUntil(this.stopped$),
            withLatestFrom(this.configObservable(FULL_STATE)),
            filter(([ , state ]) => FORWARD_TYPE.includes(state.productType)),
            map(combineForwardOrderRequest),
        ).subscribe(async (requestOrderConfig) => {
            this.notificationObs
                .next(orderToNotification(await this.placeOrderService.sendForwardOrderRequest(requestOrderConfig)));
        });

        this.orderRequestsObs.pipe(
            takeUntil(this.stopped$),
            withLatestFrom(this.configObservable(FULL_STATE)),
            filter(([ , state ]) => state.productType === 'pending'),
            map(combinePendingOrderRequest),
        ).subscribe(async (requestOrderConfig) => {
            this.notificationObs
                .next(orderToNotification(await this.placeOrderService.sendPendingOrderRequest(requestOrderConfig)));
        });

        AlkLogger.debug('[PAD_CONFIG_SERVICE] started');
    }

    update = (config: IPadConfig): void => {
        this.origConfigChange(config);
    };

    toggleFeed = (isStarted: boolean): void =>
        this.origSetConfigProperty('isStartRequested', isStarted);

    placeOrder(side: QuoteSideTypeEnum): void {
        this.orderRequestsObs.next(side);
    }

    stop(): void {
        AlkLogger.debug('[PAD_CONFIG_SERVICE] stopped');
        this.stopped$.next(true);
    }
}
